import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { optimizeImageUrlFillSquare } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Spacer } from "@faire/web/slate/spacing";
import { Span } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import { Link } from "@faire/web/ui/LinkAnchor";
import React, { type JSX } from "react";

export interface PillProps {
  pillContent: string | undefined;
  pillIcon?: JSX.Element;
  linkTo: string | undefined;
  onClick?: () => void;
  pillImage?: IImage;
}

const SQUARE_DIMENSION = 32;
export const Pill: React.FC<{
  pill: PillProps;
  style?: React.CSSProperties;
  className?: string;
}> = ({ pill, style, className }) => {
  const { pillContent, linkTo = "", onClick, pillIcon, pillImage } = pill;
  const isSSR = useIsSSR();
  const imageUrl = pillImage
    ? optimizeImageUrlFillSquare(pillImage, isSSR, SQUARE_DIMENSION, {
        ignoreResizeTag: true,
      })
    : undefined;

  if (!pillContent) {
    return null;
  }

  const pillWrapperClassName =
    "flex flex-[0_0_auto] items-center justify-center h-10 rounded-[40px] border border-core-border-muted cursor-pointer py-2.5 px-4 hover:border-core-border-default focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-webkit-focus-ring-color";
  const pillWithImageWrapperClassName = cn(
    pillWrapperClassName,
    "bg-core-surface-inverse rounded-[50px] pr-4 pl-1"
  );

  if (imageUrl) {
    return (
      <Link
        to={linkTo}
        onClick={onClick}
        className={pillWithImageWrapperClassName}
      >
        <img
          className="block h-8 w-8 rounded-full"
          src={imageUrl}
          alt={pillContent}
        />
        <Spacer width="1x" />
        <PillContent pillContent={pillContent} />
      </Link>
    );
  }

  return (
    <Link
      style={style}
      to={linkTo}
      onClick={onClick}
      className={cn(pillWrapperClassName, className)}
    >
      {pillIcon ? (
        <>
          {pillIcon}
          <Spacer width="1x" />
        </>
      ) : null}
      <PillContent pillContent={pillContent} />
    </Link>
  );
};

const PillContent: React.FC<{ pillContent: string }> = ({ pillContent }) => {
  return (
    <Span
      variant="paragraphSansRegular"
      className="overflow-hidden text-center text-ellipsis whitespace-nowrap"
    >
      {pillContent}
    </Span>
  );
};
